import React, { useEffect, useState } from 'react';
import dispatchPaperworkService, {
  DispatchPaperworkPreview,
} from '../../services/dispatchPaperwork.service';
import { findInvalidEmails } from '../../utils/email';
import { useAltHotkeys } from '../../hooks/useAltHotkeys';
import { useModalDrag } from '../../hooks/useModalDrag';
import ModalHeader from '../ui/ModalHeader';

export interface SendDispatchConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  dispatchRecno: number;
  pronumber: string;
  legnum: number;
  driverUnit: string;
}

type Phase = 'loading' | 'idle' | 'sending' | 'success' | 'error';

const SendDispatchConfirmModal: React.FC<SendDispatchConfirmModalProps> = ({
  isOpen,
  onClose,
  dispatchRecno,
  pronumber,
  legnum,
  driverUnit,
}) => {
  const [phase, setPhase] = useState<Phase>('loading');
  const [preview, setPreview] = useState<DispatchPaperworkPreview | null>(null);
  const [resultMessage, setResultMessage] = useState('');

  // Override-email controls — `overriding` toggles the input UI; `altEmail`
  // is what the user typed. Empty string + not-overriding means "use the
  // preview email as-is".
  const [overriding, setOverriding] = useState(false);
  const [altEmail, setAltEmail] = useState('');

  useEffect(() => {
    if (!isOpen) {
      // Reset whenever the modal closes so reopening starts fresh.
      setPhase('loading');
      setPreview(null);
      setResultMessage('');
      setOverriding(false);
      setAltEmail('');
      return;
    }
    if (!dispatchRecno) {
      setPhase('error');
      setResultMessage('Missing dispatch record — assign a driver before sending paperwork.');
      return;
    }
    let cancelled = false;
    setPhase('loading');
    dispatchPaperworkService
      .preview(dispatchRecno)
      .then((p) => {
        if (cancelled) return;
        setPreview(p);
        setPhase('idle');
      })
      .catch((err) => {
        if (cancelled) return;
        setResultMessage(
          err instanceof Error ? err.message : 'Failed to load driver details.',
        );
        setPhase('error');
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, dispatchRecno]);

  // Alt+C closes, but not mid-send — same guard the backdrop click uses below.
  // Send itself gets no chord: it fires paperwork to the driver.
  useAltHotkeys({ C: phase === 'sending' ? null : onClose }, undefined, { layer: isOpen });

  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(isOpen);

  if (!isOpen) return null;

  const driverEmail = preview?.driver_email ?? '';
  const effectiveDriverUnit = preview?.driver_unit || driverUnit;
  const effectiveLegnum = preview?.legnum ?? legnum;
  const effectivePronumber = preview?.pronumber || pronumber;
  const effectiveRecipient = overriding ? altEmail.trim() : driverEmail;
  const altEmailInvalid = overriding && findInvalidEmails(altEmail, { semicolon: true }).length > 0;
  const sendDisabled = !effectiveRecipient || altEmailInvalid || phase === 'sending';

  const handleSend = async () => {
    setPhase('sending');
    try {
      const result = await dispatchPaperworkService.sendToDriver(
        dispatchRecno,
        overriding ? altEmail.trim() : undefined,
      );
      setResultMessage(result.message);
      setPhase(result.success ? 'success' : 'error');
    } catch (err) {
      setResultMessage(
        err instanceof Error ? err.message : 'Send failed — check console for details.',
      );
      setPhase('error');
      console.error('Send dispatch paperwork failed', err);
    }
  };

  const backdropClick = phase === 'sending' ? undefined : onClose;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center" role="dialog" aria-label="Send dispatch paperwork">
      <div className="absolute inset-0 bg-scrim/50" onClick={backdropClick} />
      <div
        ref={panelRef}
        style={panelStyle}
        className="relative bg-panel rounded-lg shadow-xl max-w-md w-full mx-4"
      >
        <ModalHeader
          title={phase === 'success' ? 'Paperwork Sent' : phase === 'error' ? 'Send Failed' : 'Send Paperwork To Driver'}
          onClose={backdropClick}
          dragHandleProps={dragHandleProps}
        />
        <div className="p-6">

        {phase === 'loading' && (
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-6 flex items-center gap-2">
            <svg className="w-4 h-4 animate-spin text-slate-500" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeOpacity="0.25" />
              <path d="M22 12a10 10 0 01-10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
            </svg>
            Loading driver info...
          </p>
        )}

        {phase === 'idle' && (
          <div className="mb-6 space-y-3">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Email dispatch paperwork for Pro <span>{effectivePronumber}</span>,
              Leg {effectiveLegnum} to driver{' '}
              <span>{effectiveDriverUnit || '(unassigned)'}</span>.
            </p>

            {!overriding ? (
              <div className="flex items-center justify-between gap-3 rounded-md border border-slate-200 dark:border-gray-700 bg-slate-50 dark:bg-gray-900 px-3 py-2">
                <div className="min-w-0">
                  <div className="text-xs uppercase text-slate-500 dark:text-slate-400">Send to</div>
                  <div className="text-sm text-slate-800 dark:text-slate-100 truncate">
                    {driverEmail || <span className="italic text-amber-700 dark:text-amber-300">No email on file</span>}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setOverriding(true);
                    setAltEmail(driverEmail || '');
                  }}
                  className="shrink-0 px-3 py-1.5 text-xs font-medium text-indigo-700 dark:text-indigo-300 bg-panel border border-indigo-300 dark:border-indigo-700 rounded-md hover:bg-indigo-50 dark:hover:bg-gray-700"
                  data-testid="send-dispatch-use-alt"
                >
                  Use different email
                </button>
              </div>
            ) : (
              <div className="rounded-md border border-slate-200 dark:border-gray-700 bg-slate-50 dark:bg-gray-900 px-3 py-2">
                <div className="flex items-center justify-between mb-1">
                  <label htmlFor="send-dispatch-alt-email" className="text-xs uppercase text-slate-500 dark:text-slate-400">
                    Send to (override)
                  </label>
                  {driverEmail && (
                    <button
                      type="button"
                      onClick={() => {
                        setOverriding(false);
                        setAltEmail('');
                      }}
                      className="text-xs text-indigo-700 dark:text-indigo-300 hover:underline"
                    >
                      Use driver email
                    </button>
                  )}
                </div>
                <input
                  id="send-dispatch-alt-email"
                  type="email"
                  value={altEmail}
                  onChange={(e) => setAltEmail(e.target.value)}
                  placeholder="someone@example.com"
                  className="w-full text-sm px-2 py-1.5 rounded border border-slate-300 dark:border-gray-600 bg-panel text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  autoFocus
                  data-testid="send-dispatch-alt-email"
                />
                {altEmailInvalid && (
                  <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                    Enter a valid email address.
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {phase === 'sending' && (
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-6 flex items-center gap-2">
            <svg className="w-4 h-4 animate-spin text-slate-500" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeOpacity="0.25" />
              <path d="M22 12a10 10 0 01-10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
            </svg>
            Sending paperwork to <span>{effectiveRecipient}</span>...
          </p>
        )}

        {phase === 'success' && (
          <p className="text-sm text-emerald-700 dark:text-emerald-300 mb-6 flex items-start gap-2">
            <svg className="w-5 h-5 mt-0.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
            <span>{resultMessage}</span>
          </p>
        )}

        {phase === 'error' && (
          <p className="text-sm text-red-700 dark:text-red-300 mb-6 flex items-start gap-2">
            <svg className="w-5 h-5 mt-0.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
            <span>{resultMessage}</span>
          </p>
        )}

        <div className="flex justify-end gap-3">
          {(phase === 'loading' || phase === 'idle') && (
            <>
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-label bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={handleSend}
                disabled={phase === 'loading' || sendDisabled}
                className={`px-4 py-2 text-sm font-medium text-white rounded-md ${
                  phase === 'loading' || sendDisabled
                    ? 'bg-indigo-400 cursor-not-allowed'
                    : 'bg-indigo-600 hover:bg-indigo-700'
                }`}
                data-testid="send-dispatch-confirm"
              >
                Send
              </button>
            </>
          )}
          {phase === 'sending' && (
            <button
              disabled
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-400 rounded-md cursor-not-allowed"
            >
              Sending...
            </button>
          )}
          {(phase === 'success' || phase === 'error') && (
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-label bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600"
              autoFocus
            >
              Close
            </button>
          )}
        </div>
        </div>
      </div>
    </div>
  );
};

export default SendDispatchConfirmModal;
